import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import startCase from "lodash/startCase";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import sortBy from "lodash/sortBy";

import { Box, Typography } from "@mui/material";
import PaymentMethodSetup, {
  PaymentMethodSetupHandle,
  PaymentMethodSetupState,
  SavedPaymentMethod,
} from "components/payment/PaymentMethodSetup/PaymentMethodSetup";
import {
  clearProfileSetup,
  ProfileSetupReturn,
  retainProfileSetup,
  removeSetupReturnParams,
  ProfileSetupCorrelation,
} from "components/profile/paymentMethodSetupReturn";

import RHFStripeCreditCardInput, {
  validateStripeCreditCard,
} from "components/react-hook-form/RHFStripeCreditCardInput";
import {
  CREATE_PAYMENT_METHOD_MUTATION,
  LOAD_ME_QUERY,
} from "../../globals/graphql";
import MoovsDialog from "../globals/MoovsDialog";
import {
  useAnalytics,
  useCurrentUser,
  useLaunchDarklyFlags,
  useSnackbar,
  useOperator,
} from "../../globals/hooks";
import { getErrorMessage } from "../../moovsErrors/getErrorMessage";
import { RHFTextField } from "../react-hook-form/text-field/RHFTextField";
import { CreateLinkedPassengerDialog } from "pages/passengers/components";
import PassengerRHFAutocomplete from "../react-hook-form/autocomplete/PassengerRHFAutocomplete";

export type AddCreditCardDialogProps = {
  open: boolean;
  setAddCreditCardDialogOpen: Dispatch<SetStateAction<boolean>>;
  setStripePaymentMethodId?: Dispatch<SetStateAction<string>>;
  onSetPaymentMethod?: Dispatch<SetStateAction<string>>;
  setPreferredPaymentMethodId?: Dispatch<SetStateAction<string>>;
  trackingName?: string;
  paymentElementContext?: { operatorId: string; payerId: string };
  paymentElementReturn?: ProfileSetupReturn;
  onPaymentElementReturnCleared?: () => void;
  onSavedPaymentMethod?: (method: SavedPaymentMethod) => void;
  onBeforePaymentElementConfirm?: (
    correlation: ProfileSetupCorrelation
  ) => void;
};
const errorSchema = yup.object({
  name: yup.string().required("Please enter cardholder name"),
  creditCard: validateStripeCreditCard,
  linkToCreditCardPassenger: yup
    .object({
      id: yup.string(),
      firstName: yup.string(),
      lastName: yup.string(),
      email: yup.string(),
      mobilePhone: yup.string().required(),
    })
    .nullable(),
});

function AddCreditCardDialog(props: AddCreditCardDialogProps) {
  const {
    open,
    setAddCreditCardDialogOpen,
    setStripePaymentMethodId,
    setPreferredPaymentMethodId,
    onSetPaymentMethod,
    trackingName,
    paymentElementContext,
    paymentElementReturn,
    onPaymentElementReturnCleared,
    onSavedPaymentMethod,
    onBeforePaymentElementConfirm,
  } = props;

  // hooks
  const stripeElements = useElements();
  const stripe = useStripe();
  const snackbar = useSnackbar();
  const { track } = useAnalytics();
  const currentUser = useCurrentUser();
  const { operator } = useOperator();
  const { enableLinkedPassenger } = useLaunchDarklyFlags();
  const client = useApolloClient();
  const location = useLocation();
  const setupRef = useRef<PaymentMethodSetupHandle>(null);
  const setupIntentIdRef = useRef<string | null>(null);
  const submittedSetupRef = useRef<PaymentMethodSetupHandle | null>(null);
  const [setupState, setSetupState] = useState<PaymentMethodSetupState | null>(
    null
  );
  const usePaymentElement = !!paymentElementContext;
  const returnSetupIntentId = paymentElementReturn?.setupIntentId;

  const passengers = currentUser
    ? sortBy(
        currentUser.linkedPassengers.map((passenger) => {
          return {
            id: passenger.id,
            email: passenger.email,
            mobilePhone: passenger.mobilePhone,
            firstName: startCase(passenger.firstName),
            lastName: startCase(passenger.lastName),
          };
        }),
        ["firstName", "lastName"]
      )
    : [];

  // state
  const [linkedPassengerDialogOpen, setLinkedPassengerDialogOpen] =
    useState(false);

  // form state
  const formId = `add-credit-card-dialog-${currentUser.id}`;
  const methods = useForm<{
    name: string;
    creditCard: {
      type: string;
      message: string;
    };
    linkToCreditCardPassenger: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      mobilePhone: string;
    };
  }>({
    defaultValues: {
      name: "",
      creditCard: {},
      linkToCreditCardPassenger: null,
    },
    mode: "onSubmit",
    resolver: yupResolver(
      paymentElementReturn
        ? yup.object({})
        : usePaymentElement
          ? errorSchema.omit(["creditCard"])
          : errorSchema
    ),
  });

  const { name, linkToCreditCardPassenger } = methods.watch();

  const handleSavedPaymentMethod = (method: SavedPaymentMethod) => {
    const { id, stripeId, card } = method;
    const { last4, brand } = card;
    setStripePaymentMethodId?.(stripeId);
    onSetPaymentMethod?.(id);
    setPreferredPaymentMethodId?.(id);
    onSavedPaymentMethod?.(method);
    snackbar.success(`${startCase(brand)} ****-${last4} has been added`);
    if (trackingName) track(trackingName);
    handleClose();
  };

  // mutations
  const [createPaymentMethod, { loading: createPaymentLoading }] = useMutation(
    CREATE_PAYMENT_METHOD_MUTATION,
    {
      onCompleted(data) {
        handleSavedPaymentMethod(data.createPaymentMethod.paymentMethod);
      },
      refetchQueries: [{ query: LOAD_ME_QUERY }],
      onError(error) {
        const errorMessage =
          getErrorMessage(error) || "Error creating payment.";

        snackbar.error(errorMessage);
      },
    }
  );

  // event handlers
  const handleClose = () => {
    setAddCreditCardDialogOpen(false);
    if (paymentElementReturn && setupState?.restartRequired) {
      clearProfileSetup(paymentElementReturn.setupIntentId);
      onPaymentElementReturnCleared?.();
    }
  };

  const createStripePaymentMethod = async () => {
    const {
      paymentMethod: stripePaymentMethod,
      error: stripePaymentMethodError,
    } = await stripe.createPaymentMethod({
      type: "card",
      card: stripeElements.getElement(CardElement),
      billing_details: {
        name: methods.getValues().name,
      },
    });

    if (stripePaymentMethodError) {
      snackbar.error(stripePaymentMethodError.message);
      return;
    }

    createPaymentMethod({
      variables: {
        input: {
          contactId: currentUser.id,
          stripePaymentMethod,
          ...(linkToCreditCardPassenger && {
            linkedPassengerId: linkToCreditCardPassenger.id,
          }),
        },
      },
    });
  };

  const savePaymentElement = async () => {
    const setup = setupRef.current;
    if (!setup || submittedSetupRef.current === setup) return;
    submittedSetupRef.current = setup;
    try {
      const paymentMethod = await setup.save();
      if (setupRef.current !== setup) return;
      if (setupIntentIdRef.current || paymentElementReturn) {
        clearProfileSetup(
          setupIntentIdRef.current || paymentElementReturn.setupIntentId
        );
      }
      // The card is already saved even if refreshing the list fails.
      client.refetchQueries({ include: [LOAD_ME_QUERY] }).catch(() => {
        snackbar.error(
          "Card saved. Please refresh to update your cards on file."
        );
      });
      handleSavedPaymentMethod(paymentMethod);
      onPaymentElementReturnCleared?.();
    } catch (error) {
      if (submittedSetupRef.current === setup) submittedSetupRef.current = null;
      if (setupRef.current !== setup) return;
      snackbar.error(
        getErrorMessage(error) ||
          (error instanceof Error
            ? error.message
            : "Unable to save card. Please try again.")
      );
    }
  };
  const savePaymentElementRef = useRef(savePaymentElement);
  savePaymentElementRef.current = savePaymentElement;

  // effects
  useEffect(() => {
    // reset values on open
    if (open) {
      methods.reset();
      setSetupState(null);
      setupIntentIdRef.current = null;
    }
  }, [methods, open]);

  useEffect(() => {
    if (open && returnSetupIntentId && setupState?.ready) {
      savePaymentElementRef.current();
    }
  }, [open, returnSetupIntentId, setupState?.ready]);

  const handleAddNewPassengerClick = () => {
    setLinkedPassengerDialogOpen(true);
  };

  const setSelectedPassenger = (newPassenger) => {
    methods.setValue("linkToCreditCardPassenger", newPassenger);
  };

  return (
    <>
      <MoovsDialog
        hideTopBorder
        size="xs"
        dialogTitle="Add Credit Card"
        acceptButtonText="Add"
        open={open}
        onClose={handleClose}
        dialogMarginTop={usePaymentElement ? undefined : "-20vh"}
        fixedFooter={usePaymentElement}
        isXsFullscreenMobile={usePaymentElement}
        acceptDisabled={
          createPaymentLoading ||
          methods.formState.isSubmitting ||
          (usePaymentElement && setupState?.busy)
        }
        submitFormId={formId}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              methods.handleSubmit(
                usePaymentElement
                  ? savePaymentElement
                  : createStripePaymentMethod
              )(e);
            }}
            id={formId}
          >
            {!paymentElementReturn && (
              <RHFTextField
                name="name"
                fullWidth
                variant="outlined"
                label="Cardholder name"
                {...(usePaymentElement && {
                  inputProps: { style: { fontSize: 16 } },
                })}
              />
            )}
            <Box my={1.5}>
              {usePaymentElement ? (
                open && (
                  <>
                    {paymentElementReturn && (
                      <Typography>
                        Checking card verification. If saving fails, select Add
                        to retry.
                      </Typography>
                    )}
                    <PaymentMethodSetup
                      ref={setupRef}
                      operatorId={paymentElementContext.operatorId}
                      currency={operator.settings?.currencyCode || "USD"}
                      context={{
                        surface: "profile",
                        payerId: paymentElementContext.payerId,
                      }}
                      billingDetails={{
                        name,
                        ...(currentUser.email && { email: currentUser.email }),
                        ...(currentUser.mobilePhoneInternational && {
                          phone: currentUser.mobilePhoneInternational,
                        }),
                      }}
                      returnUrl={`${location.pathname}${removeSetupReturnParams(location.search)}`}
                      resume={paymentElementReturn}
                      finalizeOptions={
                        paymentElementReturn
                          ? {
                              linkedPassengerId:
                                paymentElementReturn.linkedPassengerId,
                            }
                          : {
                              linkedPassengerId: enableLinkedPassenger
                                ? linkToCreditCardPassenger?.id
                                : undefined,
                            }
                      }
                      onBeforeConfirm={(setupIntentId, options) => {
                        const correlation = {
                          ...paymentElementContext,
                          setupIntentId,
                          returnPath: location.pathname,
                          linkedPassengerId: options?.linkedPassengerId,
                        };
                        retainProfileSetup(correlation);
                        onBeforePaymentElementConfirm?.(correlation);
                        setupIntentIdRef.current = setupIntentId;
                      }}
                      onStateChange={setSetupState}
                    />
                  </>
                )
              ) : (
                <RHFStripeCreditCardInput name="creditCard" />
              )}
            </Box>

            {enableLinkedPassenger && !paymentElementReturn ? (
              <PassengerRHFAutocomplete
                passengers={passengers}
                handleAddNewPassengerClick={handleAddNewPassengerClick}
                linkToCreditCardPassengerId={linkToCreditCardPassenger?.id}
              />
            ) : null}
          </form>
        </FormProvider>
      </MoovsDialog>

      {enableLinkedPassenger ? (
        <CreateLinkedPassengerDialog
          open={linkedPassengerDialogOpen}
          setLinkedPassengerDialogOpen={setLinkedPassengerDialogOpen}
          onCreateLinkedPassenger={setSelectedPassenger}
        />
      ) : null}
    </>
  );
}

export default AddCreditCardDialog;
